// components/NotificationSendModal.js
import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Plus,
  Trash2,
  Loader2,
  Check,
  AlertCircle,
  Share2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "./ui/switch";
import { extractArticleIdFromUrl } from "../lib/utils";

const baseUrl =
  process.env.REACT_APP_NOTIFICATION_BASE_URL || "http://localhost:8080";

const MAX = {
  TITLE: 100,
  SUMMARY: 200,
  BTN_TITLE: 30,
  BTN_URL: 200,
  URL: 2000,
};

export default function NotificationSendModal({
  isOpen,
  onClose,
  onSave,
  error: parentError,
  domainInfo,
  topics = [],
  topicsLoading = false,
  topicsError = null,
}) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [url, setUrl] = useState("");
  const [banner, setBanner] = useState("");
  const [image, setImage] = useState("");
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [actionButtons, setActionButtons] = useState([]);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shareSwitch, setShareSwitch] = useState({ facebook: false });
  const [formErrors, setFormErrors] = useState({
    url: "",
    title: "",
    summary: "",
    selectedCampaigns: "",
    utmSource: "",
    utmMedium: "",
    utmCampaign: "",
    actionButtons: [],
  });
  const SHARE_PLATFORMS = [
    { id: "whatsapp", label: "WhatsApp", icon: "W" },
    { id: "facebook", label: "Facebook", icon: "F" },
    { id: "twitter", label: "Twitter/X", icon: "X" },
    { id: "linkedin", label: "LinkedIn", icon: "in" },
    { id: "telegram", label: "Telegram", icon: "T" },
  ];

  // Prefill UTM & Banner from domainInfo
  useEffect(() => {
    if (!domainInfo) return;
    setUtmSource((prev) => prev || domainInfo.utmSource || "app");
    setUtmMedium((prev) => prev || domainInfo.utmMedium || "notification");
    setUtmCampaign((prev) => prev || domainInfo.utmCampaign || "");
    setImage(domainInfo.image || "");
  }, [domainInfo]);
  // Metadata Scraper (debounced)
  useEffect(() => {
    let timer;
    const fetchMetadata = async () => {
      if (!url || !/^https?:\/\//i.test(url)) return;
      setIsFetchingMetadata(true);
      setMetadataError(null);
      try {
        const res = await fetch(
          `${baseUrl}notification/scrape-metadata?url=${encodeURIComponent(
            url
          )}`
        );
        if (!res.ok) throw new Error("Failed to fetch metadata");
        const json = await res.json();
        if (json.status !== 200) throw new Error(json.message ?? "API error");

        const { title: metaTitle, description, ogImage } = json.data;
        if (metaTitle && !title) setTitle(metaTitle.slice(0, MAX.TITLE));
        if (description && !summary)
          setSummary(description.slice(0, MAX.SUMMARY));
        if (ogImage && !banner) setBanner(ogImage);
        if (!utmCampaign) {
          setUtmCampaign(extractArticleIdFromUrl(url));
        }
      } catch (e) {
        setMetadataError(e.message);
      } finally {
        setIsFetchingMetadata(false);
      }
    };
    timer = setTimeout(fetchMetadata, 600);
    return () => clearTimeout(timer);
  }, [url]);

  // Reset form
  const resetForm = useCallback(() => {
    setTitle("");
    setSummary("");
    setUrl("");
    setBanner("");
    setImage(domainInfo?.image || "");
    setSelectedTopicIds([]);
    setUtmSource(domainInfo?.utmSource || "");
    setUtmMedium(domainInfo?.utmMedium || "");
    setUtmCampaign(domainInfo?.utmCampaign || "");
    setActionButtons([]);
    setMetadataError(null);
    setFormErrors({
      url: "",
      title: "",
      summary: "",
      selectedCampaigns: "",
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
      actionButtons: [],
    });
  }, [domainInfo]);

  // Toggle campaign
  const toggleTopic = (id) => {
    setSelectedTopicIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setFormErrors((e) => ({ ...e, selectedCampaigns: "" }));
  };

  // Action buttons
  const addButton = () => {
    if (actionButtons.length >= 2) return;
    setActionButtons((b) => [
      ...b,
      { title: "", url: "", action: "btn" + (actionButtons.length + 1) },
    ]);
    setFormErrors((e) => ({
      ...e,
      actionButtons: [...e.actionButtons, { title: "", url: "" }],
    }));
  };

  const removeButton = (idx) => {
    setActionButtons((b) => b.filter((_, i) => i !== idx));
    setFormErrors((e) => ({
      ...e,
      actionButtons: e.actionButtons.filter((_, i) => i !== idx),
    }));
  };

  const updateButton = (idx, field, value) => {
    const limit = field === "title" ? MAX.BTN_TITLE : MAX.BTN_URL;
    const newButtons = [...actionButtons];
    newButtons[idx][field] = value.slice(0, limit);
    setActionButtons(newButtons);

    setFormErrors((e) => {
      const newErr = [...e.actionButtons];
      newErr[idx] = { ...newErr[idx], [field]: "" };
      return { ...e, actionButtons: newErr };
    });
  };

  // Generic field change
  const changeField = (setter, errorKey, value, max = Infinity) => {
    setter(value.slice(0, max));
    setFormErrors((e) => ({ ...e, [errorKey]: "" }));
  };

  // Validation
  const validate = () => {
    const err = {
      url: !url.trim()
        ? "URL is required"
        : url.length > MAX.URL
        ? `URL too long (max ${MAX.URL})`
        : "",
      title: !title.trim()
        ? "Title is required"
        : title.length > MAX.TITLE
        ? `Title too long (max ${MAX.TITLE})`
        : "",
      summary: !summary.trim()
        ? "Summary is required"
        : summary.length > MAX.SUMMARY
        ? `Summary too long (max ${MAX.SUMMARY})`
        : "",
      selectedCampaigns:
        selectedTopicIds.length === 0 ? "Select at least one campaign" : "",
      // utmSource: !utmSource.trim() ? "UTM Source required" : "",
      // utmMedium: !utmMedium.trim() ? "UTM Medium required" : "",
      utmCampaign: "",
      actionButtons: actionButtons.map((b) => ({
        title: b.shareId
          ? ""
          : !b.title.trim()
          ? "Button title required"
          : b.title.length > MAX.BTN_TITLE
          ? `Title too long (max ${MAX.BTN_TITLE})`
          : "",
        url: b.shareId
          ? ""
          : !b.url.trim()
          ? "Button URL required"
          : b.url.length > MAX.BTN_URL
          ? `URL too long (max ${MAX.BTN_URL})`
          : "",
      })),
    };
    setFormErrors(err);
    const btnOk = err.actionButtons.every((e) => !e.title && !e.url);
    return (
      !err.url &&
      !err.title &&
      !err.summary &&
      !err.selectedCampaigns &&
      // !err.utmSource &&
      // !err.utmMedium &&
      btnOk
    );
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      // === 1. Extract ID from URL (e.g., 40017458 from .html) ===
      let id = null;
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const match = pathname.match(/(\d+)\.html$/); // Match digits before .html
        if (match) {
          id = match[1]; // e.g., "40017458"
        }
      } catch (err) {
        console.warn("Invalid URL for ID extraction:", url);
      }

      // === 2. Build UTM params ===
      const utmParams = new URLSearchParams();

      if (utmSource) utmParams.append("utm_source", utmSource);
      if (utmMedium) utmParams.append("utm_medium", utmMedium);

      // Use custom campaign OR fallback to ID
      const campaignValue = utmCampaign || (id ? id : null);
      if (campaignValue) {
        utmParams.append("utm_campaign", campaignValue);
      }

      // === 3. Build final URL ===
      let finalUrl = url;
      if (utmParams.toString()) {
        const separator = url.includes("?") ? "&" : "?";
        finalUrl = `${url}${separator}${utmParams.toString()}`;
      }

      const processedButtons = actionButtons.map((btn, index) => {
        if (btn.shareId) {
          const shareUrl = buildShareUrl(
            btn.shareId,
            finalUrl,
            `${title} – ${summary}`
          );
          return {
            title: btn.title,
            url: shareUrl,
            action: `btn${index + 1}`,
          };
        }
        return {
          title: btn.title,
          url: btn.url,
          action: btn.action,
        };
      });

      await onSave({
        title,
        body: summary,
        url: finalUrl,
        banner,
        img: image,
        topicId: selectedTopicIds,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign, // keep original (may be empty)
        btn_list: processedButtons,
      });

      resetForm();
      // onClose();
    } catch (error) {
      console.error("Submit failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const updateShareSwitch = () => {};
  /* ------------------- SHARE SWITCH LOGIC ------------------- */
  const toggleShare = (idx, platformId) => {
    const btn = actionButtons[idx];
    if (!btn) return;

    const newButtons = [...actionButtons];

    // If the button is already a share button for a *different* platform → just switch
    if (btn.shareId && btn.shareId !== platformId) {
      newButtons[idx] = {
        ...newButtons[idx],
        shareId: platformId,
        title: platformId === "twitter" ? "Tweet" : `Share on ${platformId}`,
        url: "",
      };
      setActionButtons(newButtons);
      return;
    }

    // Count how many share buttons are currently ON
    const enabledCount = actionButtons.filter((b) => !!b.shareId).length;

    if (btn.shareId === platformId) {
      // ---- TURN OFF ----
      newButtons[idx] = {
        title: "",
        url: "",
        action: btn.action.startsWith("btn") ? btn.action : `btn${idx + 1}`,
      };
    } else {
      // ---- TURN ON ----
      if (enabledCount >= 2) {
        // disable the oldest enabled share
        const oldestIdx = actionButtons.findIndex((b) => !!b.shareId);
        if (oldestIdx !== -1 && oldestIdx !== idx) {
          newButtons[oldestIdx] = {
            title: "",
            url: "",
            action: btn.action.startsWith("btn") ? btn.action : `btn${idx + 1}`,
          };
        }
      }

      newButtons[idx] = {
        ...newButtons[idx],
        shareId: platformId,
        title:
          platformId === "twitter" ? "Share on X" : `Share on ${platformId}`,
        url: "",
        action: btn.action.startsWith("btn") ? btn.action : `btn${idx + 1}`,
      };
    }

    setActionButtons(newButtons);
  };
  const buildShareUrl = (platform, pageUrl, text) => {
    const encUrl = encodeURIComponent(pageUrl);
    const encText = encodeURIComponent(text);
    const map = {
      whatsapp: `https://api.whatsapp.com/send?text=${encText}%20${encUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encUrl}&text=${encText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      telegram: `https://t.me/share/url?url=${encUrl}&text=${encText}`,
    };
    return map[platform];
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col md:flex-row items-center justify-center p-4 overflow-y-auto gap-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-card rounded-2xl shadow-xl min-h-[400px] w-full max-w-2xl border border-border my-8 h-[90%] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">
            Send Notification
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-secondary transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {parentError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {parentError}
            </div>
          )}
          {metadataError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {metadataError}
            </div>
          )}
          {isFetchingMetadata && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Fetching metadata...
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* URL */}
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) =>
                  changeField(setUrl, "url", e.target.value, MAX.URL)
                }
                placeholder="https://example.com/article"
              />
              {formErrors.url && (
                <p className="text-sm text-red-600">{formErrors.url}</p>
              )}
            </div>

            {/* Title */}
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="title">
                Notification Title * (max {MAX.TITLE})
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) =>
                  changeField(setTitle, "title", e.target.value, MAX.TITLE)
                }
                placeholder="Enter title"
              />
              <p
                className={`text-sm ${
                  formErrors.title ? "text-red-600" : "text-muted-foreground"
                }`}
              >
                {formErrors.title || `${title.length}/${MAX.TITLE}`}
              </p>
            </div>

            {/* Summary */}
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="summary">Summary * (max {MAX.SUMMARY})</Label>
              <Textarea
                id="summary"
                rows={4}
                value={summary}
                onChange={(e) =>
                  changeField(
                    setSummary,
                    "summary",
                    e.target.value,
                    MAX.SUMMARY
                  )
                }
                placeholder="Enter summary"
              />
              <p
                className={`text-sm ${
                  formErrors.summary ? "text-red-600" : "text-muted-foreground"
                }`}
              >
                {formErrors.summary || `${summary.length}/${MAX.SUMMARY}`}
              </p>
            </div>

            {/* Banner & Image */}
            <div className="space-y-2">
              <Label htmlFor="banner">Banner Image URL</Label>
              <Input
                id="banner"
                type="url"
                value={banner}
                onChange={(e) => setBanner(e.target.value)}
                placeholder="https://example.com/banner.jpg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="image">Notification Image URL</Label>
              <Input
                id="image"
                type="url"
                value={image}
                onChange={(e) => setImage(e.target.value)}
                placeholder="https://example.com/image.jpg"
                disabled
              />
            </div>

            {/* Campaigns */}
            <div className="md:col-span-2 space-y-2">
              <Label>Campaigns *</Label>

              {topicsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading campaigns...
                </div>
              ) : topicsError ? (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {topicsError}
                </div>
              ) : topics.length === 0 ? (
                <p className="text-muted-foreground">
                  No campaigns found for this domain.
                </p>
              ) : (
                <>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between h-auto min-h-[40px] text-left font-normal"
                      >
                        {selectedTopicIds.length === 0
                          ? "Select campaigns"
                          : `${selectedTopicIds.length} campaign(s) selected`}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <div className="max-h-64 overflow-y-auto p-2">
                        {topics.map((t) => (
                          <label
                            key={t.id}
                            className="flex items-center space-x-2 p-2 hover:bg-secondary rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedTopicIds.includes(t.id)}
                              onCheckedChange={() => toggleTopic(t.id)}
                            />
                            <span>{t.name}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {formErrors.selectedCampaigns && (
                    <p className="text-sm text-red-600">
                      {formErrors.selectedCampaigns}
                    </p>
                  )}

                  {selectedTopicIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedTopicIds.map((id) => {
                        const topic = topics.find((t) => t.id === id);
                        return topic ? (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                          >
                            {topic.name}
                            <button
                              type="button"
                              onClick={() => toggleTopic(id)}
                              className="hover:bg-primary/20 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* UTM */}
            <div className="space-y-2">
              <Label htmlFor="utm_source">UTM Source</Label>
              <Input
                id="utm_source"
                value={utmSource}
                onChange={(e) =>
                  changeField(setUtmSource, "utmSource", e.target.value)
                }
                placeholder="app"
              />
              {formErrors.utmSource && (
                <p className="text-sm text-red-600">{formErrors.utmSource}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="utm_medium">UTM Medium</Label>
              <Input
                id="utm_medium"
                value={utmMedium}
                onChange={(e) =>
                  changeField(setUtmMedium, "utmMedium", e.target.value)
                }
                placeholder="notification"
              />
              {formErrors.utmMedium && (
                <p className="text-sm text-red-600">{formErrors.utmMedium}</p>
              )}
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="utm_campaign">UTM Campaign</Label>
              <Input
                id="utm_campaign"
                value={utmCampaign}
                onChange={(e) =>
                  changeField(setUtmCampaign, "utmCampaign", e.target.value)
                }
                placeholder="e.g. summer_sale"
              />
            </div>
          </div>

          {/* Action Buttons */}
          {/* ---- Action Buttons ---- */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Action Buttons (max 2)</Label>
              {actionButtons.length < 2 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addButton}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Button
                </Button>
              )}
            </div>

            {actionButtons.map((btn, i) => (
              <div
                key={i}
                className="flex gap-3 p-4 border border-border rounded-lg"
              >
                <div className="flex-1 space-y-3">
                  {/* Normal fields – hidden when share is active */}
                  {!btn.shareId && (
                    <>
                      <div className="space-y-1">
                        <Input
                          placeholder="Button title"
                          value={btn.title}
                          onChange={(e) =>
                            updateButton(i, "title", e.target.value)
                          }
                        />
                        <p
                          className={`text-sm ${
                            formErrors.actionButtons[i]?.title
                              ? "text-red-600"
                              : "text-muted-foreground"
                          }`}
                        >
                          {formErrors.actionButtons[i]?.title ||
                            `${btn.title.length}/${MAX.BTN_TITLE}`}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <Input
                          type="url"
                          placeholder="Button URL"
                          value={btn.url}
                          onChange={(e) =>
                            updateButton(i, "url", e.target.value)
                          }
                        />
                        <p
                          className={`text-sm ${
                            formErrors.actionButtons[i]?.url
                              ? "text-red-600"
                              : "text-muted-foreground"
                          }`}
                        >
                          {formErrors.actionButtons[i]?.url ||
                            `${btn.url.length}/${MAX.BTN_URL}`}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Share switches */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Share2 className="h-4 w-4 text-muted-foreground" />
                    {SHARE_PLATFORMS.map((p) => (
                      <label
                        key={p.id}
                        className="flex items-center gap-1 cursor-pointer"
                      >
                        <Switch
                          checked={btn.shareId === p.id}
                          onCheckedChange={() => toggleShare(i, p.id)}
                        />
                        <span className="text-xs">{p.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Show auto-generated title */}
                  {btn.shareId && (
                    <p className="text-sm text-muted-foreground">{btn.title}</p>
                  )}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeButton(i)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          {/* Footer */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              className="flex-1"
              disabled={
                isSubmitting ||
                isFetchingMetadata ||
                title.length > MAX.TITLE ||
                summary.length > MAX.SUMMARY ||
                actionButtons.some(
                  (b) =>
                    b.title.length > MAX.BTN_TITLE || b.url.length > MAX.BTN_URL
                )
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Send Now
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      <div className="relative bg-[#f1f1f2] px-4 py-4 w-[500px]">
        {banner && (
          <div className="mb-2">
            <img
              width={"100%"}
              height={100}
              src={banner ?? "/default.png"}
              tile="banner"
              alt="banner"
              style={{ height: "200px" }}
            />
          </div>
        )}
        <div className="flex flex-row gap-2 ">
          <div className="w-[20%]">
            {" "}
            <img
              height={150}
              width={150}
              src={image ?? "/default.png"}
              title="logo"
              alt="logo"
            />
          </div>
          <div className="w-[75%]">
            <h2 className="text-xl font-bold text-foreground wrap ">
              {title ||
                "Lorem ipsum dolor sit amet consectetur adipiscing elit quisque "}
            </h2>
            <p className="text-sm text-gray">
              {summary ||
                "Lorem ipsum dolor sit amet consectetur adipiscing elit quisque Lorem ipsum dolor sit amet consectetur adipiscing elit quisque"}
            </p>
          </div>
        </div>
        {actionButtons.length > 0 && (
          <div className="flex flex-row gap-2 mt-3">
            {actionButtons.map((el, i) => {
              const displayTitle =
                el.shareId && el.title ? el.title : el.title || "Button";
              return (
                <a
                  key={i}
                  href={el.shareId ? "#" : el.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#f5f5f5] border border-gray-300 shadow flex-grow text-center py-2 rounded text-sm"
                >
                  {displayTitle}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
